/**
 * 表单校验工具函数集合
 * 用于统一管理表单验证逻辑，提高代码复用性
 */

/**
 * 安全的字符串去空格处理
 * @param {*} value - 任意类型的值
 * @returns {string} - 去除首尾空格后的字符串
 */
export const safeTrim = (value) => {
  return (value ?? "").toString().trim();
};

/**
 * 检查字符串是否以非字母数字的特殊符号开头
 * @param {string} str - 待检查的字符串
 * @returns {boolean} - 是否以特殊符号开头
 */
export const startsWithNonAlphanumericSymbol = (str) => {
  // 匹配常见键盘非字母数字符号（涵盖中英文符号）
  return /^[!@#$%^&*()_+\-=[\]\\{}|;:',.<>/?！@#￥%……&*（）——+\-=【】、；：''，。《》？]/.test(str);
};

/**
 * 检查字符串中是否包含括号（全角或半角）
 * @param {string} str - 待检查的字符串
 * @returns {boolean} - 是否包含括号
 */
export const hasBrackets = (str) => {
  const bracketRegex = /[()（）]/;
  return bracketRegex.test(str);
};

/**
 * 检查软件名称后缀是否符合规范
 * @param {string} softwareName - 软件全称
 * @param {number} type - 软件类型 (0:PC, 1:Android, 2:iOS, 3:小程序, 4:嵌入式)
 * @returns {boolean} - 后缀是否合规
 */
export const checkSoftwareSuffix = (softwareName, type) => {
  // 正则说明：
  // 匹配以下结尾：
  // 1. 系统、平台、软件（纯关键词）
  // 2. 系统(xxx)、平台(yyy)、软件(zzz)（半角括号）
  // 3. 系统（xxx）、平台（yyy）、软件（zzz）（全角中文括号）

  let regex;

  if (type === 0 || type === 4) {
    // PC端和嵌入式：必须以 系统/平台/软件 结尾
    regex = /(系统|平台|软件)(?:\([^)]*\)|（[^）]*）)?$/;
  } else if (type === 1 || type === 2) {
    // Android和iOS：可以以 系统/平台/软件/APP/客户端 结尾
    regex = /(系统|平台|软件|APP|客户端)(?:\([^)]*\)|（[^）]*）)?$/;
  } else if (type === 3) {
    // 小程序：可以以 系统/平台/软件/小程序 结尾
    regex = /(系统|平台|软件|小程序)(?:\([^)]*\)|（[^）]*）)?$/;
  }

  return regex.test(softwareName);
};

/**
 * 检测软件名称中的APP相关关键词
 * @param {string} name - 软件名称
 * @returns {number} - 返回检测结果码
 *   0: 无匹配
 *   1: 安卓相关
 *   2: 苹果相关
 *   3: 小程序相关
 *   21: APP关键词
 */
export const hasAppKeywords = (name) => {
  if (typeof name !== 'string') {
    return 0; // 非字符串输入返回0
  }

  // 定义各类关键词的正则表达式（不区分大小写）
  // 安卓相关关键词（优先级1）
  const androidPatterns = [
    /安卓版/i,
    /android\s*版/i,
    /android/i
  ];

  // 苹果相关关键词（优先级2）
  const iosPatterns = [
    /ios\s*版/i,
    /苹果版/i,
    /ios/i
  ];

  // APP关键词（优先级3）
  const appPattern = /app/i;

  // 小程序关键词（优先级3）
  const miniappPattern = /小程序/i;

  // 先检查安卓相关关键词
  if (androidPatterns.some(pattern => pattern.test(name))) {
    return 1;
  }

  // 再检查苹果相关关键词
  if (iosPatterns.some(pattern => pattern.test(name))) {
    return 2;
  }

  // 然后检查APP关键词
  if (appPattern.test(name)) {
    return 21;
  }

  // 最后检查小程序关键词
  if (miniappPattern.test(name)) {
    return 3;
  }

  // 都不匹配返回0
  return 0;
};

/**
 * 创建软件名称校验器
 * @param {Object} softForm - 软件表单对象
 * @param {Object} refs - 包含showSoftwareNameWarning、showApp、softwareNameErr的响应式引用
 * @param {Object} formRef - 表单引用
 * @returns {Function} - 校验器函数
 */
export const createSoftwareNameValidator = (softForm, refs, formRef) => {
  return (rule, value, callback) => {
    const { showSoftwareNameWarning, showApp, softwareNameErr } = refs;

    // 检查：软件全称不能为空
    if (!value.trim().length) {
      showSoftwareNameWarning.value = false;
      showApp.value = 0;
      softwareNameErr.value = true;
      return callback(new Error("软件全称不能为空"));
    }

    // 检查：软件全称不能过短（至少4个字符）
    if (value.trim().length < 4) {
      showSoftwareNameWarning.value = false;
      showApp.value = 0;
      softwareNameErr.value = true;
      return callback(new Error("软件全称过短，不能低于3个字符"));
    }

    // 检查：PC端和嵌入式软件名称后缀规范
    if ((softForm.type === 0 || softForm.type === 4) &&
        !checkSoftwareSuffix(value.toString().trim(), softForm.type)) {
      showSoftwareNameWarning.value = false;
      showApp.value = 0;
      softwareNameErr.value = true;
      return callback(new Error('软件全称必须以软件、平台、系统结尾'));
    }

    // 检查：Android和iOS软件名称后缀规范
    if ((softForm.type === 1 || softForm.type === 2) &&
        !checkSoftwareSuffix(value.toString().trim(), softForm.type)) {
      showSoftwareNameWarning.value = false;
      showApp.value = 0;
      softwareNameErr.value = true;
      return callback(new Error('软件全称必须以软件、平台、系统、APP、客户端结尾'));
    }

    // 检查：小程序软件名称后缀规范
    if ((softForm.type === 3) &&
        !checkSoftwareSuffix(value.toString().trim(), softForm.type)) {
      showSoftwareNameWarning.value = false;
      showApp.value = 0;
      softwareNameErr.value = true;
      return callback(new Error('软件全称必须以软件、平台、系统、小程序结尾'));
    }

    // 检查：软件全称不能以特殊符号开头
    if (startsWithNonAlphanumericSymbol(value.toString().trim())) {
      showSoftwareNameWarning.value = false;
      showApp.value = 0;
      softwareNameErr.value = true;
      return callback(new Error("软件全称不能以特殊符号开头"));
    }

    // 所有检查通过，清除错误状态
    softwareNameErr.value = false;

    // 如果填写了品牌名称，同时校验品牌名称字段
    if (softForm.brand.trim()) {
      formRef.value.validateField("brand");
    }

    callback();
  };
};

/**
 * 创建品牌名称校验器
 * @param {Object} softForm - 软件表单对象
 * @returns {Function} - 校验器函数
 */
export const createBrandValidator = (softForm) => {
  return (rule, value, callback) => {
    // 核心校验：softwareName是否包含brand的内容（不区分大小写）
    const isContain = softForm.softwareName?.trim().toLowerCase().includes(value.trim().toLowerCase());

    if (isContain) {
      callback(); // 校验通过
    } else {
      callback(new Error(`软件名称必须包含品牌名称：${value}`));
    }
  };
};

/**
 * 创建软件简称校验器
 * @param {Object} softForm - 软件表单对象
 * @returns {Function} - 校验器函数
 */
export const createShortNameValidator = (softForm) => {
  return (rule, value, callback) => {
    // 检查：软件简称不能与软件全称一致
    if (softForm.softwareName.trim().length &&
        softForm.softwareName.trim() === value.trim()) {
      return callback(new Error("软件简称不能与软件全称一致"));
    }

    // 检查：软件简称长度不能超过软件全称
    if (softForm.softwareName.trim().length &&
        softForm.softwareName.trim().length < value.trim().length) {
      return callback(new Error("软件简称长度不能超过软件全称"));
    }

    callback();
  };
};

/**
 * 创建版本号校验器
 * @returns {Function} - 校验器函数
 */
export const createVersionValidator = () => {
  return (rule, value, callback) => {
    // 检查：版本号不能为空
    if (!value.trim().length) {
      return callback(new Error("版本号不能为空"));
    }

    // 版本号格式正则：支持 1.0、1.0.0 等格式
    const regex = /^[+]?(0|([1-9]\d*))(\.\d+)(\.\d+)?$/g;
    // 带V前缀的版本号格式：V1.0、V1.0.0 等
    const regex1 = /^V\d+(\.\d+)*$/;

    if (!(regex.test(value) || regex1.test(value))) {
      return callback(new Error("输入无效，请输入正确版本号如V1.0（大写的V）"));
    }

    callback();
  };
};

/**
 * 创建著作权人校验器（支持重复检测）
 * @param {number} index - 著作权人索引
 * @param {Object} softForm - 软件表单对象
 * @param {Function} resetWarningCallback - 重置警告的回调函数
 * @returns {Function} - 校验器函数
 */
export const createApplicantValidator = (index, softForm, resetWarningCallback) => {
  return (rule, value, callback) => {
    // 检查：著作权人不能为空
    if (!safeTrim(value)) {
      callback(new Error('著作权人不能为空'));
    }

    // 检查：著作权人不能重复
    if (safeTrim(value) &&
        softForm.applicants.filter(a => safeTrim(a.name) === safeTrim(value)).length > 1) {
      // 重置该索引的警告状态
      if (resetWarningCallback) {
        resetWarningCallback(index);
      }
      callback(new Error('著作权人不能重复'));
    }

    callback();
  };
};

/**
 * 通用必填字段校验规则
 */
export const requiredRules = {
  description: [{ required: true, message: "关键词不能为空", trigger: "blur" }],
  businessScope: [{ required: true, message: "经营范围不能为空", trigger: "blur" }],
  phone: [{ required: true, message: "手机号不能为空", trigger: "blur" }],
  username: [{ required: true, message: "用户名不能为空", trigger: "blur" }],
  password: [{ required: true, message: "密码不能为空", trigger: "blur" }]
};
